import React, { useEffect, useState } from 'react';

const HotelPhoto = ({ hotel, className }) => {
  const photos = (hotel.images?.length ? hotel.images : [{ url: hotel.image }]).filter((photo) => photo?.url);
  const [index, setIndex] = useState(0);
  const photo = photos[index];

  if (!photo) return null;

  return (
    <img
      src={photo.url}
      alt={hotel.name || ''}
      referrerPolicy="no-referrer"
      className={className}
      onError={() => {
        setIndex((current) => (current + 1 < photos.length ? current + 1 : current));
      }}
    />
  );
};

const CDN = 'https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets';

const EDITORIAL = {
  lead: `${CDN}/landing_page/refine/Cap-Rocat-Stage-M.jpg`,
  coast: `${CDN}/landing_page/refine/New-Hotels-Mallorca-Square-2.jpg`,
  city: `${CDN}/landing_page/refine/loacations/london.jpeg`,
  resort: `${CDN}/IDK-REQ/atlantis-the-palmjpg-1b19e1d1-b721-471b-b737-a08aea6cf0f5.jpg`,
  evening: `${CDN}/IDK-REQ/pexels-lina-12238284.jpg`,
};

const notes = [
  {
    label: 'Place',
    title: 'Houses with a sense of where they are',
    text: 'Villas, island reserves, and quiet coastal hotels chosen because the setting still feels private.',
  },
  {
    label: 'Pace',
    title: 'Arrivals that are allowed to be slow',
    text: 'The better stays leave room between the journey and the first evening. Nothing is rushed into a highlight.',
  },
  {
    label: 'Care',
    title: 'Service that stays in the background',
    text: 'For clients who want the room, the light, and the table handled well, without the stay announcing itself.',
  },
];

const HotelCatalog = ({ hotels, loading }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const featured = hotels.slice(0, 8);
  const active = featured[activeIndex] || null;

  useEffect(() => {
    setActiveIndex(0);
  }, [hotels.length]);

  useEffect(() => {
    if (paused || featured.length < 2) return undefined;
    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % featured.length);
    }, 6500);
    return () => clearInterval(timer);
  }, [paused, featured.length]);

  const goTo = (index) => {
    if (!featured.length) return;
    const next = (index + featured.length) % featured.length;
    setActiveIndex(next);
  };

  if (loading) {
    return (
      <section className="relative z-0 py-10 sm:py-14" style={{ backgroundColor: '#f7f7f7' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-28 h-2 bg-gray-200 mb-3 animate-pulse" />
          <div className="w-64 h-7 bg-gray-200 mb-8 animate-pulse" />
          <div className="h-[22rem] sm:h-[28rem] bg-gray-200 animate-pulse" />
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-gray-200 animate-pulse" />
            <div className="space-y-3 pt-4">
              <div className="w-24 h-2 bg-gray-200 animate-pulse" />
              <div className="w-3/4 h-8 bg-gray-200 animate-pulse" />
              <div className="w-full h-3 bg-gray-200 animate-pulse" />
              <div className="w-5/6 h-3 bg-gray-200 animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!hotels.length) return null;

  return (
    <section className="relative z-0 py-10 sm:py-14" style={{ backgroundColor: '#f7f7f7' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8 flex items-end justify-between gap-4">
          <div>
            <p className="text-[9px] tracking-[0.28em] uppercase font-poppinsLight text-gray-400 mb-2">
              The collection
            </p>
            <h2 className="text-2xl sm:text-3xl text-gray-900 font-poppinsLight tracking-wide">
              Hotels to explore
            </h2>
          </div>
          <p className="hidden sm:block text-[11px] tracking-wide text-gray-400 font-poppinsLight max-w-xs text-right">
            A selection from the Axonall collection. Search above for live availability.
          </p>
        </div>

        {active && (
          <div
            className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="relative overflow-hidden bg-gray-900 h-[22rem] sm:h-[30rem]">
              {featured.map((hotel, index) => (
                <div
                  key={hotel.id || hotel.hotelCode}
                  className={`absolute inset-0 text-left transition-opacity duration-700 ${
                    index === activeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
                  }`}
                  aria-hidden={index !== activeIndex}
                >
                  <HotelPhoto hotel={hotel} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/10" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 text-white">
                    <div className="flex items-center gap-3 mb-2">
                      {hotel.tier && (
                        <span className="text-[9px] tracking-[0.22em] uppercase border border-white/40 px-2 py-1">
                          {hotel.tier}
                        </span>
                      )}
                      <span className="text-[11px] tracking-wide text-white/80 font-poppinsLight">
                        {[hotel.city, hotel.country].filter(Boolean).join(', ')}
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-4xl font-poppinsLight tracking-wide">{hotel.name}</h3>
                    {hotel.description && (
                      <p className="mt-2 max-w-2xl text-sm text-white/80 font-poppinsLight line-clamp-2">
                        {hotel.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {featured.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous hotel"
                    onClick={() => goTo(activeIndex - 1)}
                    className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 text-gray-900 flex items-center justify-center hover:bg-white"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label="Next hotel"
                    onClick={() => goTo(activeIndex + 1)}
                    className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 text-gray-900 flex items-center justify-center hover:bg-white"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>

            {featured.length > 1 && (
              <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                {featured.map((hotel, index) => (
                  <button
                    key={`thumb-${hotel.id || hotel.hotelCode}`}
                    type="button"
                    onClick={() => goTo(index)}
                    aria-label={`Show ${hotel.name}`}
                    className={`relative flex-shrink-0 w-24 sm:w-32 h-16 overflow-hidden border ${
                      index === activeIndex ? 'border-gray-900' : 'border-transparent opacity-70 hover:opacity-100'
                    }`}
                  >
                    <HotelPhoto hotel={hotel} className="absolute inset-0 w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-16 sm:mt-20 border-t border-gray-200 pt-12 sm:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-end mb-10">
            <div className="lg:col-span-7">
              <p className="text-[9px] tracking-[0.28em] uppercase font-poppinsLight text-gray-400 mb-3">
                The way we travel
              </p>
              <h3 className="text-3xl sm:text-5xl text-gray-900 font-poppinsLight tracking-wide leading-tight">
                A quieter kind of luxury
              </h3>
            </div>
            <p className="lg:col-span-5 text-sm sm:text-base text-gray-500 font-poppinsLight leading-relaxed">
              The stays worth offering are rarely the loudest. They are houses with a sense of place,
              coastlines that still feel private, and city hotels that know how to disappear into the evening.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4">
            <div className="md:col-span-7 relative min-h-[22rem] sm:min-h-[32rem] overflow-hidden bg-gray-200">
              <img
                src={EDITORIAL.lead}
                alt="A stone hotel above the coast"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="md:col-span-5 grid grid-rows-2 gap-3 sm:gap-4 min-h-[22rem]">
              <div className="relative overflow-hidden bg-gray-200 min-h-[12rem]">
                <img
                  src={EDITORIAL.coast}
                  alt="A Mallorca hotel terrace"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="relative overflow-hidden bg-gray-200 min-h-[12rem]">
                <img
                  src={EDITORIAL.city}
                  alt="London in the evening"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {notes.map((note) => (
              <div key={note.label} className="border-t border-gray-200 pt-5">
                <p className="text-[9px] tracking-[0.24em] uppercase font-poppinsLight text-gray-400 mb-3">
                  {note.label}
                </p>
                <h4 className="text-lg text-gray-900 font-poppinsLight tracking-wide mb-2">
                  {note.title}
                </h4>
                <p className="text-sm text-gray-500 font-poppinsLight leading-relaxed">
                  {note.text}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 sm:mt-16 grid grid-cols-1 lg:grid-cols-2 min-h-[24rem]">
            <div className="relative min-h-[18rem] overflow-hidden bg-gray-900">
              <img
                src={EDITORIAL.resort}
                alt="A waterfront resort at dusk"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="bg-[#111111] text-white px-8 sm:px-12 py-12 sm:py-16 flex flex-col justify-center">
              <p className="text-[9px] tracking-[0.28em] uppercase text-white/50 font-poppinsLight mb-4">
                For the journey
              </p>
              <h3 className="text-3xl sm:text-4xl font-poppinsLight tracking-wide leading-tight mb-5">
                Search when the dates are known. Stay with what feels right before then.
              </h3>
              <p className="text-sm text-white/70 font-poppinsLight leading-relaxed max-w-md">
                The collection above is here to look through. Live availability, rates, and rooms
                begin only when you search.
              </p>
              <img
                src={EDITORIAL.evening}
                alt=""
                className="mt-8 h-28 w-44 object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HotelCatalog;
